import { compareVersions } from 'compare-versions';
import { cloneDeep, flatten, isArray } from 'lodash';
import queryString from 'query-string';
import { v4 as uuid } from 'uuid';
import { Message, Modal } from '@meitu/saas-ui';
import { Editor } from '@meitu/saas-editor-core';
import { TextMarkStruct } from '@meitu/saas-editor-core/es/layers/text-layer';
import { LayerCanvasCreator } from '@meitu/saas-editor-core/es/layers/vector-layer/utils';
import {
    getGraphText,
    getVectorTextFromCache,
    serializeSvgText,
    stringToJson,
    uploadVectorFile,
} from '@meitu/saas-editor-core/es/utils/file-kit';
import { transformArtTextDataToApp } from '@meitu/saas-editor-core/es/utils/transform-kit';
import { traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import { uploadBase64ToObs, uploadFileToObs } from '@meitu/saas-editor-core/es/utils/upload-kit';
import { getDefaultClientBackgroundValue } from '@meitu/saas-editor-core/es/version-parse';
import {
    CanvasType,
    IBackgroundLayerProps,
    ILayerProps,
    ImgCategoryType,
    IResult,
    ITextContent,
    ITextLayerProps,
    IVectorLayerProps,
    LayerType,
    StageType,
    VectorType,
} from '@meitu/saas-editor-core/es/version-types';
import { DownloadImgType } from '@meitu/saas-editor-ui/es/constants';
import { formatTextArtLayer, TextArtThumbnailSize } from '@/editor/panel/views/text-art-panel/utils/text-art-opera';
import { setTextMarkSvg, TextMarkThumbnailSize } from '@/editor/panel/views/text-mark-panel/utils/text-mark-opera';
import {
    isAiPPT,
    isAITemplate,
    isEditAIPPTComponent,
    isEditAIPPTTemplate,
    isEditMaterial,
    isEditorWatermark,
    isEditTextArt,
    isEditTextMark,
    isEmptyTemplate,
    isEmptyTextTemplate,
    isTextTemplate,
    isUploadAIPPTComponent,
    isUploadAIPPTTemplate,
    isUploadTextMaterialPsd,
    isUploadWatermark,
    noUploadTemplatePsdAsEdit,
} from '@/hooks/fetch-template/editor-mode';
import {
    divideMainLayer,
    getBaseTemplate,
    parseClientToServerLayers,
    stageModelToDesignData,
    stageModelToTextTemplateData,
} from '@/hooks/save-template/client-to-server';
import { createAIPPTComponent, createAIPPTTemplate, editAIPPTComponent, editAIPPTTemplate } from '@/services/ai';
import { updateSvgMaterial } from '@/services/material';
import {
    createTemplate,
    createTextTemplate,
    createWatermarkTemplate,
    editTemplate,
    editTextTemplate,
    editWatermarkTemplate,
    saveDraftTemplate,
    templateCheck,
    updateAITemplate,
} from '@/services/template';
import { createTextArt, createTextMark, updateTextArt, updateTextMark } from '@/services/text';
import { ITextArtConfig } from '@/store/models/text-mark-model';
import { CODE } from '@/types';
import { isInternalDesignerMode } from '@/utils/auth';
import { getPreviewImage } from '@/utils/download-image';
import { appendQueryParams, deleteQueryParams, getQuery, getQueryParam, removeAllQueryAndAddId } from '@/utils/query';

const getPolygonText = (text: string, data: any) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'image/svg+xml');
    const svgElement = doc.documentElement;
    //特效可以正常显示
    const oldStyle = svgElement.getAttribute('style');
    svgElement.setAttribute('style', `overflow:visible;width:100%;height:100%;${oldStyle}`);

    const shapeElements = svgElement.querySelectorAll('path, polygon, rect, ellipse,circle,line,polyline');

    shapeElements.forEach((shape) => {
        shape.setAttribute('fill', `${data.fill}`);
    });

    const serializer = new XMLSerializer();
    text = serializer.serializeToString(doc);

    return text;
};

const goBack = () => {
    if (getQueryParam('draft') === '1') {
        window.open(
            `${window.location.origin}/materials/#/TaskAuditCreate?id=${
                getQueryParam('parent_id') || getQueryParam('old_template_id')
            }&back=1`,
        );
    }
};

const hasCommercialFont = (fonts: any[], stageAllLayers: ILayerProps[]) => {
    let hasCommercialFont = false;
    traverseTrees(stageAllLayers, (child: ILayerProps) => {
        if (child.layerType === LayerType.TEXT_LAYER) {
            const textLayer = child as ILayerProps;
            const font = fonts.find((font) => font.font_name === (textLayer.data as ITextLayerProps).fontFamily);
            if ((font as any)?.is_commercial === 0) {
                hasCommercialFont = true;
            }

            if (
                (textLayer.data as ITextLayerProps).textContents &&
                ((textLayer.data as ITextLayerProps).textContents as ITextContent[]).length > 0
            ) {
                (textLayer.data as ITextLayerProps).textContents?.forEach((item) => {
                    const font = fonts.find((font) => font.font_name === item.fontFamily);
                    if ((font as any)?.is_commercial === 0) {
                        hasCommercialFont = true;
                    }
                });
            }
        }
    });

    return hasCommercialFont;
};
const saveTemplate = async (
    editor: Editor,
    templateName: string,
    resolution: number,
    canvasType: CanvasType,
    extraInfo: any,
    psdFileUrl: string,
    useWatermark: boolean,
    setLoading: (loading: boolean) => void,
    setShowSaveFail: (showSaveFail: boolean) => void,
    setShowSaveSuccess: (showSaveSuccess: boolean) => void,
    initTemplateInfo: (templateInfo: any) => void,
    showModal = true,
    save = true,
    back = false,
    successMsg = '保存成功',
    fonts: any[] = [],
    markConfig: TextMarkStruct | null,
    textArtConfig: ITextArtConfig | null,
) => {
    // 保存模板
    try {
        editor.tip.showMask({
            text: '保存中...',
            loading: true,
        });

        const stageType = editor.stage.getType();
        const unit = editor.stage.getUnit();
        const stages = await editor.download.getResult(false);
        const stageAllLayers = flatten(stages.map((item) => item.layers));

        let hasHiddenLayer = false;
        traverseTrees(stageAllLayers, (child: ILayerProps) => {
            if (child.hidden) {
                hasHiddenLayer = true;
            }
        });

        // 有隐藏图层不让保存
        if (hasHiddenLayer) {
            Message.error('请先关闭隐藏图层');

            editor.tip.hideMask();
            setLoading(false);
            return;
        }
        const clientDataResultStages = cloneDeep(editor.stage.getStages()).map((item) => {
            return {
                width: item[0].width,
                height: item[0].height,
                layers: item,
            };
        });
        let version =
            stageType === StageType.VARIETY || (stageType === StageType.SINGLE && stages.length > 1)
                ? '9.0.0'
                : '1.0.0';
        const run = async () => {
            if (isAiPPT()) {
                traverseTrees(stageAllLayers, (child: ILayerProps) => {
                    if (!child.extraInfo) {
                        child.extraInfo = {};
                    }
                });
            }

            // 防止版本太高 生成的花字素材在pc版本太高了
            traverseTrees(stageAllLayers, (child: ILayerProps) => {
                if (child.extraInfo && child.extraInfo.modified && Object.keys(child.extraInfo).length === 1) {
                    child.extraInfo = undefined;
                }
            });

            const { layers: serviceLayers, version: currentVersion } = parseClientToServerLayers(
                stageAllLayers,
                stageType === StageType.VARIETY || (stageType === StageType.SINGLE && stages.length > 1),
                { unit, resolution },
            );

            let index = 0;
            for (let i = 0; i < stages.length; i++) {
                const element = stages[i];
                for (let j = 0; j < element.layers.length; j++) {
                    element.layers[j] = serviceLayers[index] as any;
                    index = index + 1;
                }
            }

            if (compareVersions(currentVersion, version) >= 0) {
                version = currentVersion;
            }

            const { slices } = await editor.download.drawStages(
                undefined,
                DownloadImgType.jpg,
                undefined,
                false,
                false,
                false,
            );

            const firstPreview = slices[0];

            if (canvasType === CanvasType.PPT && !isUploadAIPPTComponent() && !isEditAIPPTComponent()) {
                // ppt类型的生成的封面是5页的
                const cover = await editor.download.drawPPTCover(slices, isAiPPT());
                slices[0] = {
                    width: cover.width,
                    height: cover.height,
                    canvas: cover,
                };
            }

            const allPreviews = [] as { url: string; width: number; height: number }[];

            for (let i = 0; i < stages.length; i++) {
                const preview = await getPreviewImage(slices[i].canvas, DownloadImgType.jpg, '', {
                    width: slices[i].canvas.width,
                    height: slices[i].canvas.height,
                });
                allPreviews.push(preview);
            }

            const baseTemplate = getBaseTemplate({
                preview: allPreviews[0].url,
                name: templateName,
                version,
                templateConf: compareVersions(version, '7.0.0') >= 0 ? stages : stages[0],
                editMode: canvasType,
                unit,
                resolution,
            });

            let preview = baseTemplate.preview;

            console.log('baseTemplate===>', baseTemplate);
            const checkRsp = await templateCheck({
                formula: JSON.stringify(baseTemplate),
            });

            if (checkRsp.errors && checkRsp.errors.length > 0) {
                Message.error('版本字段校验未通过， 请联系开发人员');
                console.log(JSON.stringify(checkRsp.errors));
                editor.tip.hideMask();
                setLoading(false);
                return;
            }

            if ((isEditAIPPTComponent() || isEditAIPPTTemplate()) && extraInfo) {
                baseTemplate.extraInfo = extraInfo;
                const result = stringToJson(baseTemplate);

                const { url } = await uploadFileToObs(result, undefined, undefined, 'json');

                extraInfo.aiPPTJSON = url;

                if (isEditAIPPTTemplate()) {
                    const pptPreview = await uploadBase64ToObs(firstPreview.canvas.toDataURL('image/png', 0.8));
                    extraInfo.fullPreview = preview;
                    preview = pptPreview.url;
                    baseTemplate.preview = pptPreview.url;
                }
            }

            const params = stageModelToDesignData(baseTemplate, canvasType, preview, allPreviews, extraInfo);

            if (isInternalDesignerMode()) {
                params.use_watermark = useWatermark;
            }

            const saveMaterial = async (
                previews: {
                    width: number;
                    height: number;
                    url: string;
                }[],
                tip?: string,
            ) => {
                // 编辑花字模板
                const templateConf = isArray(baseTemplate.templateConf)
                    ? baseTemplate.templateConf[0]
                    : baseTemplate.templateConf;
                // 编辑花字
                const defaultBg = getDefaultClientBackgroundValue(templateConf.width, templateConf.height);
                (defaultBg.data as IBackgroundLayerProps).backgroundColor = '#00000000';
                const layer = clientDataResultStages[0].layers[1];

                if (clientDataResultStages[0].layers.length > 2) {
                    Message.error(tip || '花字素材所有图层必须成组才能保存，请先成组');
                    editor.tip.hideMask();
                    setLoading(false);
                    return;
                }

                const resultCanvas = await editor.download.drawLayers(
                    [defaultBg, ...editor.download.getDrawLayers([layer])],
                    'png',
                    false,
                );
                const { url: preview } = await uploadBase64ToObs(resultCanvas.toDataURL('image/png', 0.8));
                baseTemplate.preview = preview;
                const params = stageModelToTextTemplateData(
                    { ...baseTemplate, templateConf },
                    canvasType,
                    preview,
                    previews,
                    psdFileUrl,
                    extraInfo,
                );
                return params;
            };

            if (isEditMaterial()) {
                try {
                    const stages = editor.stage.getStages();
                    const svgLayer = stages[0].find((item) => {
                        return item.layerType === LayerType.VECTOR_LAYER;
                    });
                    if (svgLayer) {
                        let resultSvgText = '';
                        if ((svgLayer.data as IVectorLayerProps).vectorType === VectorType.Graph) {
                            const svgText = getVectorTextFromCache((svgLayer.data as IVectorLayerProps).url);
                            const text = getGraphText(svgText, (svgLayer.data as IVectorLayerProps).colors);
                            resultSvgText = serializeSvgText(text);
                        } else {
                            const { width, height } = svgLayer;
                            const {
                                naturalWidth,
                                naturalHeight,
                                strokeWidth,
                                strokeHidden,
                                borderRadius,
                                url,
                                vectorType,
                                fill,
                                fillGradient,
                                stroke,
                                strokeGradient,
                                strokeDasharray,
                                strokeLinecap,
                                blur,
                                effect,
                            } = svgLayer.data as IVectorLayerProps;
                            const svgText = getVectorTextFromCache(url);

                            resultSvgText = getPolygonText(svgText, {
                                fill,
                                fillGradient,
                                stroke,
                                strokeGradient,
                                strokeWidth,
                                strokeDasharray,
                                strokeLinecap,
                                naturalWidth,
                                naturalHeight,
                                width,
                                height,
                                blur,
                                effect,
                                id: svgLayer.id,
                                hasEffect: true,
                                strokeHidden,
                                isRect: vectorType === VectorType.Rect,
                            });
                        }
                        const { url: svg_file } = await uploadVectorFile(resultSvgText);
                        let preview = '';
                        const layerCanvasCreator = new LayerCanvasCreator(editor.id, svgLayer);
                        const layerCanvas = await layerCanvasCreator.create({ isPPT: false });
                        if (layerCanvas) {
                            const resultCanvas = document.createElement('canvas');
                            let resultWidth = layerCanvas.width;
                            let resultHeight = layerCanvas.height;
                            if (resultWidth > resultHeight) {
                                resultWidth = 420;
                                resultHeight = (resultWidth / layerCanvas.width) * layerCanvas.height;
                            } else {
                                resultHeight = 420;
                                resultWidth = (resultHeight / layerCanvas.height) * layerCanvas.width;
                            }
                            resultCanvas.width = 500;
                            resultCanvas.height = 500;
                            const ctx = resultCanvas.getContext('2d');
                            ctx?.drawImage(
                                layerCanvas,
                                0,
                                0,
                                layerCanvas.width,
                                layerCanvas.height,
                                (500 - resultWidth) / 2,
                                (500 - resultHeight) / 2,
                                resultWidth,
                                resultHeight,
                            );

                            const { url: imageUrl } = await uploadBase64ToObs(resultCanvas.toDataURL('image/png', 0.8));
                            preview = imageUrl;
                        }
                        const svg_image = (svgLayer.data as IVectorLayerProps).url;
                        const material_id = queryString.parse(window.location.search).material_id as string;

                        await updateSvgMaterial(material_id, svg_file, svg_image, preview);

                        Message.success(successMsg);
                        // 保存成功
                        editor.tip.hideMask();
                        setLoading(false);
                        return;
                    } else {
                        Message.error('没有找到svg图层，保存失败');
                    }
                } catch (error) {
                    Message.error('保存失败');
                }
            } else if (isUploadAIPPTTemplate()) {
                const rsp = await createAIPPTTemplate({
                    name: params.default_name || params.name,
                    formula: params.formula,
                    preview: preview,
                });

                Message.success(successMsg);
                window.location.href = `${window.location.origin}${window.location.pathname}?mode=internal_designer&id=${rsp.data.data.id}&isUpdateAIPPTTemplate=1`;
            } else if (isEditAIPPTTemplate()) {
                await editAIPPTTemplate({
                    name: params.default_name || params.name,
                    formula: params.formula,
                    preview: preview,
                    id: parseInt(params.id),
                });
                Message.success(successMsg);
            } else if (isUploadAIPPTComponent()) {
                const templateConf = baseTemplate.templateConf;
                if (isArray(templateConf) && extraInfo.materialType === 'ai_ppt_component') {
                    for (let i = 0; i < templateConf.length; i++) {
                        const layers = clientDataResultStages[i].layers;
                        const resultCanvas = await editor.download.drawLayers(
                            editor.download.getDrawLayers(layers),
                            'png',
                            false,
                        );
                        const { url: preview } = await uploadBase64ToObs(resultCanvas.toDataURL('image/png', 0.8));
                        await createAIPPTComponent({
                            name: params.default_name || params.name,
                            formula: JSON.stringify({
                                ...baseTemplate,
                                preview,
                                templateConf: [templateConf[i]],
                            }),
                            preview,
                            material_type: 'ai_ppt_component',
                        });
                    }
                    window.location.reload();
                } else {
                    const rsp = await createAIPPTComponent({
                        name: params.default_name || params.name,
                        formula: params.formula,
                        preview: preview,
                        material_type: extraInfo.materialType,
                        component_type: extraInfo.componentType,
                        component_points: templateConf
                            .map((item: any) => {
                                return item.extraInfo?.componentPoint || '';
                            })
                            .filter((item: string) => item)
                            .join(','),
                    });
                    window.location.href = `${window.location.origin}${window.location.pathname}?mode=internal_designer&id=${rsp.data.data.id}&isUpdateAIPPTComponent=1`;
                }
                Message.success(successMsg);
            } else if (isEditAIPPTComponent()) {
                const templateConf = baseTemplate.templateConf;
                await editAIPPTComponent({
                    name: params.default_name || params.name,
                    formula: params.formula,
                    preview: preview,
                    id: parseInt(params.id),
                    material_type: extraInfo.materialType,
                    component_type: extraInfo.componentType,
                    component_points:
                        extraInfo.materialType === 'ai_ppt_smart_component'
                            ? templateConf
                                  .map((item: any) => {
                                      return item.extraInfo?.componentPoint || '';
                                  })
                                  .filter((item: string) => item)
                                  .join(',')
                            : undefined,
                });
                Message.success(successMsg);
            } else if ((noUploadTemplatePsdAsEdit() || isEmptyTemplate()) && !isUploadTextMaterialPsd()) {
                const isEditor = getQueryParam('isEditor') === '1';
                // const task_selection = queryParams.task_selection;

                let data = null;

                if (!save) {
                    data = isEditor
                        ? await saveDraftTemplate(
                              {
                                  ...params,
                                  id: getQueryParam('template_id'),
                              },
                              queryString.stringify({
                                  ...getQuery(),
                                  is_new: getQueryParam('from') === 'ggpt' ? 1 : undefined,
                              }),
                          )
                        : await saveDraftTemplate(
                              params,
                              queryString.stringify({
                                  ...getQuery(),
                                  is_new: getQueryParam('from') === 'ggpt' ? 1 : undefined,
                              }),
                          );
                } else {
                    // 创建模板
                    data = isEditor
                        ? await editTemplate(
                              {
                                  ...params,
                                  id: getQueryParam('template_id'),
                              },
                              queryString.stringify({
                                  ...getQuery(),
                                  is_new: getQueryParam('from') === 'ggpt' ? 1 : undefined,
                              }),
                          )
                        : await createTemplate(
                              params,
                              queryString.stringify({
                                  ...getQuery(),
                                  is_new: getQueryParam('from') === 'ggpt' ? 1 : undefined,
                              }),
                          );
                }

                if (data?.data?.meta?.error) {
                    Message.error(data.data.meta.error);
                    editor.tip.hideMask();
                    setLoading(false);
                    return;
                } else if (data.data.meta?.code === CODE.MATERIAlS_CENTER) {
                    // 来源供稿流程已改变提示
                    Message.error((data.data.meta as any).msg);
                    setShowSaveFail(true);
                    editor.tip.hideMask();

                    setLoading(false);
                    return;
                }

                Message.success(successMsg);
                showModal && setShowSaveSuccess(true);
                setTimeout(() => {
                    back && goBack();
                }, 2000);

                initTemplateInfo({
                    id: isEditor ? getQueryParam('template_id') : data.data.response.data.id,
                    preview,
                    version,
                    isArray: allPreviews.length > 0,
                });
                removeAllQueryAndAddId(isEditor ? getQueryParam('template_id') : data.data.response.data.id);
            } else if (isUploadTextMaterialPsd() || isEmptyTextTemplate()) {
                // 新建花字
                const templateConf = isArray(baseTemplate.templateConf)
                    ? baseTemplate.templateConf[0]
                    : baseTemplate.templateConf;
                const defaultBg = getDefaultClientBackgroundValue(templateConf.width, templateConf.height);
                (defaultBg.data as IBackgroundLayerProps).backgroundColor = '#00000000';
                const layer = clientDataResultStages[0].layers[1];
                if (clientDataResultStages[0].layers.length > 2) {
                    Message.error('花字素材所有图层必须成组才能保存，请先成组');
                    editor.tip.hideMask();
                    setLoading(false);
                    return;
                }
                const resultCanvas = await editor.download.drawLayers(
                    [defaultBg, ...editor.download.getDrawLayers([layer])],
                    'png',
                    false,
                );
                const { url: preview } = await uploadBase64ToObs(resultCanvas.toDataURL('image/png', 0.8));
                baseTemplate.preview = preview;

                const params = stageModelToTextTemplateData(
                    { ...baseTemplate, templateConf },
                    canvasType,
                    preview,
                    allPreviews,
                    psdFileUrl,
                    extraInfo,
                );

                // 创建花字
                const data = isUploadWatermark()
                    ? await createWatermarkTemplate(
                          {
                              name: params.name,
                              preview,
                              config: params.config,
                          },
                          window.location.search.substr(1),
                      )
                    : await createTextTemplate(params, window.location.search.substr(1));
                if (data?.data?.meta?.error) {
                    Message.error(data.data.meta.error);
                    editor.tip.hideMask();
                    setLoading(false);
                    return;
                }

                if (!isUploadWatermark()) {
                    initTemplateInfo({
                        id: data.data.response.id,
                        preview,
                        version,
                        isArray: allPreviews.length > 0,
                    });
                    removeAllQueryAndAddId(data.data.response.id, true);
                } else {
                    const id = (data.data as any).data.id;
                    initTemplateInfo({
                        id,
                        preview,
                        version,
                        isArray: allPreviews.length > 0,
                    });
                    removeAllQueryAndAddId(id, false, true);
                }

                Message.success(successMsg);
                showModal && setShowSaveSuccess(true);
                setTimeout(() => {
                    back && goBack();
                }, 2000);
            } else if (isTextTemplate() || isEditorWatermark()) {
                const params = await saveMaterial(allPreviews);
                if (!params) {
                    return;
                }

                if (isTextTemplate()) {
                    const data = await editTextTemplate(params);

                    if (data?.data?.meta?.error) {
                        Message.error(data.data.meta.error);
                        editor.tip.hideMask();
                        setLoading(false);
                        return;
                    }
                } else {
                    const data = await editWatermarkTemplate({
                        name: params.name,
                        config: params.config,
                        preview: params.preview,
                        id: params.id,
                    });

                    if (data?.data?.meta?.error) {
                        Message.error(data.data.meta.error);
                        editor.tip.hideMask();
                        setLoading(false);
                        return;
                    }
                }

                Message.success(successMsg);
                showModal && setShowSaveSuccess(true);
                setTimeout(() => {
                    back && goBack();
                }, 2000);
            } else if (isAITemplate()) {
                // 编辑ai模板
                (await updateAITemplate({
                    formula: params.formula,
                    id: params.id,
                    preview: params.preview,
                })) as any;
                Message.success(successMsg);
                showModal && setShowSaveSuccess(true);
                setTimeout(() => {
                    back && goBack();
                }, 2000);
            } else if (isEditTextMark()) {
                if (!markConfig) return;
                const { layers } = clientDataResultStages[0];
                const backgroundLayer = layers[0];
                const textLayer = layers[1];
                backgroundLayer.id = uuid();
                textLayer.id = uuid();
                (textLayer.data as ITextLayerProps).text = 'ABC';

                // 缩略图尺寸
                backgroundLayer.width = TextMarkThumbnailSize.width;
                backgroundLayer.height = TextMarkThumbnailSize.height;

                // 居中
                textLayer.left = (backgroundLayer.width - textLayer.width) / 2;
                textLayer.top = (backgroundLayer.height - textLayer.height) / 2;

                let thumbnail = '';
                let thumbnailBlack = '';

                try {
                    for (let i = 0; i < 2; i++) {
                        if (i === 1) {
                            (textLayer.data as ITextLayerProps).color = '#ffffffff';
                        }

                        const originTextLayer = cloneDeep(textLayer);
                        const originBackgroundLayer = cloneDeep(backgroundLayer);
                        const cover = await editor.download.drawLayers(
                            [originBackgroundLayer, originTextLayer],
                            'png',
                            true,
                        );
                        const { url } = await uploadBase64ToObs(cover.toDataURL('image/png', 0.8));
                        if (i === 1) {
                            thumbnail = url;
                        } else {
                            thumbnailBlack = url;
                        }

                        // 缩略图强制黑色字
                        if (markConfig.useBlackText) {
                            thumbnail = thumbnailBlack;
                        }
                    }

                    if (!params.id) {
                        // 创建划重点
                        const {
                            data: {
                                data: { id },
                            },
                        } = await createTextMark({
                            material_conf: JSON.stringify(markConfig.data),
                            default_name: params.default_name,
                            thumbnail,
                            thumbnail_black: thumbnailBlack,
                            animate: markConfig.animate || 0,
                            use_black_text: markConfig.useBlackText || 0,
                            svg_url: markConfig.data.src,
                        });

                        deleteQueryParams('uploadType');
                        appendQueryParams({ id });
                        const newMarkConfig = {
                            ...markConfig,
                            materialId: `${id}`,
                        };
                        setTextMarkSvg(newMarkConfig, markConfig.materialId);
                    } else {
                        // 更新划重点
                        await updateTextMark({
                            id: params.id,
                            material_conf: JSON.stringify(markConfig.data),
                            default_name: params.default_name,
                            thumbnail,
                            thumbnail_black: thumbnailBlack,
                            animate: markConfig.animate || 0,
                            use_black_text: markConfig.useBlackText || 0,
                            svg_url: markConfig.data.src,
                        });
                    }
                } catch (error) {
                    Message.error('保存失败, error');
                }

                Message.success(successMsg);
            } else if (isEditTextArt()) {
                if (!textArtConfig) return;
                const { layers } = clientDataResultStages[0];
                const backgroundLayer = layers[0];
                const textLayer = layers[1];
                const originTextLayer = cloneDeep(textLayer);
                backgroundLayer.id = uuid();
                textLayer.id = uuid();

                // 缩略图尺寸
                backgroundLayer.width = TextArtThumbnailSize.width;
                backgroundLayer.height = TextArtThumbnailSize.height;

                // 居中
                textLayer.left = (backgroundLayer.width - textLayer.width) / 2;
                textLayer.top = (backgroundLayer.height - textLayer.height) / 2;

                let thumbnail = '';
                let thumbnailBlack = '';

                try {
                    for (let i = 0; i < 2; i++) {
                        if (i === 1) {
                            const { textContents } = textLayer.data as ITextLayerProps;
                            textContents?.forEach((el) => {
                                el.color = '#ffffffff';
                            });
                        }

                        const originTextLayer = cloneDeep(textLayer);
                        const originBackgroundLayer = cloneDeep(backgroundLayer);
                        const cover = await editor.download.drawLayers(
                            [originBackgroundLayer, originTextLayer],
                            'png',
                            true,
                        );
                        const { url } = await uploadBase64ToObs(cover.toDataURL('image/png', 0.8));
                        if (i === 1) {
                            thumbnail = url;
                        } else {
                            thumbnailBlack = url;
                        }

                        // 缩略图强制黑色字
                        if (textArtConfig.useBlackText) {
                            thumbnail = thumbnailBlack;
                        }
                    }

                    const layer = formatTextArtLayer(originTextLayer);

                    const advancedTextMaterial = (layer.data as ITextLayerProps).advancedTextMaterial;

                    const default_text = (layer.data as ITextLayerProps).text;

                    if (!advancedTextMaterial) return;

                    if (!params.id) {
                        // 创建文字特效
                        const {
                            data: {
                                data: { id },
                            },
                        } = await createTextArt({
                            config: JSON.stringify(transformArtTextDataToApp(advancedTextMaterial)),
                            default_name: params.default_name,
                            thumbnail,
                            thumbnail_black: thumbnailBlack,
                            use_black_text: textArtConfig.useBlackText || 0,
                            default_text,
                            version,
                        });
                        deleteQueryParams('uploadType');
                        appendQueryParams({ id });
                    } else {
                        advancedTextMaterial.id = params.id;
                        // 更新文字特效
                        await updateTextArt({
                            id: params.id,
                            config: JSON.stringify(transformArtTextDataToApp(advancedTextMaterial)),
                            default_name: params.default_name,
                            thumbnail,
                            thumbnail_black: thumbnailBlack,
                            use_black_text: textArtConfig.useBlackText || 0,
                            default_text,
                            version,
                        });
                    }
                } catch (error) {
                    Message.error('保存失败, error');
                }

                Message.success(successMsg);
            } else {
                // 保存模板
                const template_id = getQueryParam('template_id');
                const from = getQueryParam('from');
                const old_template_id = getQueryParam('old_template_id');
                let query = '';
                if (from === 'hbmb') {
                    query = `id=${template_id}`;
                }

                let extra = '';

                const getForegroundAndBackground = async (stage: IResult) => {
                    const { layers, width, height } = stage;

                    let mainLayer: null | ILayerProps = null;
                    traverseTrees(layers, (child: ILayerProps) => {
                        if (child.category === ImgCategoryType.mainImg) {
                            mainLayer = child;
                        }
                    });

                    if (!mainLayer) {
                        return '';
                    }
                    const [leftLayers, rightLayers] = divideMainLayer(layers, mainLayer);

                    const defaultBg = getDefaultClientBackgroundValue(width, height);
                    (defaultBg.data as IBackgroundLayerProps).backgroundColor = '#00000000';

                    const coverLeft = await editor.download.drawLayers(
                        editor.download.getDrawLayers(leftLayers),
                        'png',
                        false,
                    );
                    const coverRight = await editor.download.drawLayers(
                        [defaultBg, ...editor.download.getDrawLayers(rightLayers)],
                        'png',
                        false,
                    );

                    const { url: previewLeft } = await uploadBase64ToObs(coverLeft.toDataURL('image/png'));

                    const { url: previewRight } = await uploadBase64ToObs(coverRight.toDataURL('image/png'));

                    return JSON.stringify({
                        backgroundImageUrl: previewLeft,
                        foregroundImageUrl: previewRight,
                        mainLayer,
                    });
                };

                if (clientDataResultStages.length === 1) {
                    extra = await getForegroundAndBackground(clientDataResultStages[0]);
                }

                let result: any = {};

                // 如果是外部设计师
                if (!save) {
                    result = await saveDraftTemplate(
                        {
                            ...params,
                            id: template_id,
                            template_id: old_template_id,
                            extra,
                        },
                        queryString.stringify({
                            ...getQuery(),
                            is_new: from === 'ggpt' ? 1 : undefined,
                        }),
                    );
                } else {
                    result = (await editTemplate({ ...params, extra }, query)) as any;
                }

                if (result?.data?.meta?.error) {
                    Message.error(result.data.meta.error);
                } else if (result.data.meta?.code === CODE.MATERIAlS_CENTER) {
                    // 来源供稿流程已改变提示
                    Message.error(result.data.meta.msg);
                    setShowSaveFail(true);
                } else {
                    Message.success(successMsg);
                    showModal && setShowSaveSuccess(true);
                    setTimeout(() => {
                        back && goBack();
                    }, 2000);
                }
            }

            // 保存成功
            editor.tip.hideMask();
            setLoading(false);
        };

        if (hasCommercialFont(fonts, stageAllLayers)) {
            Modal.confirm({
                title: '提示',
                content: '当前模板使用了商用字体，是否继续保存？',
                onOk: () => {
                    run();
                },
                onCancel: () => {
                    editor.tip.hideMask();
                    setLoading(false);
                },
            });
        } else {
            run();
        }
    } catch (error) {
        Message.error((error as any).message || '保存失败请联系开发人员');
        editor.tip.hideMask();
        setLoading(false);
    }
};

export default saveTemplate;
